import * as Sentry from '@sentry/react';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import { InfoCircle, OpenNewWindow } from 'iconoir-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import { connectHubSpotPrivateApp } from '../services/IntegrationsService';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (connectedAccountId: string) => void;
}

const HUBSPOT_PRIVATE_APPS_DOCS =
  'https://developers.hubspot.com/docs/api/private-apps';

/**
 * Modal for connecting a HubSpot Private App using an access token
 */
export default function ConnectHubSpotPrivateAppModal({
  open,
  onClose,
  onSuccess,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { show } = useSnackbarContext();

  const [name, setName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string>();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName('');
      setAccessToken('');
      setConnecting(false);
      setError(undefined);
    }
  }, [open]);

  const handleConnect = useCallback(async () => {
    if (!selectedAccount) {
      setError('No account selected');
      return;
    }

    if (!name.trim()) {
      setError('Please enter a name for this connection');
      return;
    }

    if (!accessToken.trim()) {
      setError('Please enter your Private App access token');
      return;
    }

    setConnecting(true);
    setError(undefined);

    try {
      const result = await connectHubSpotPrivateApp(
        selectedAccount.id,
        accessToken.trim(),
        name.trim(),
      );

      show('HubSpot Private App connected successfully', 'success');
      onSuccess?.(result.connectedAccountId);
      onClose();
    } catch (err) {
      console.error('Failed to connect HubSpot Private App:', err);
      Sentry.captureException(err);

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to connect';

      // Provide user-friendly error messages
      if (
        errorMessage.includes('Invalid or revoked') ||
        errorMessage.includes('401')
      ) {
        setError(
          'Invalid or revoked token. Please check that your Private App token is correct and has not been revoked.',
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setConnecting(false);
    }
  }, [selectedAccount, name, accessToken, show, onSuccess, onClose]);

  const actions: ModalActionData[] = [
    {
      title: 'Cancel',
      onClick: onClose,
      type: 'secondary',
      disabled: connecting,
    },
    {
      title: connecting ? 'Connecting...' : 'Connect',
      onClick: handleConnect,
      type: 'primary',
      disabled: connecting || !name.trim() || !accessToken.trim(),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Connect HubSpot Private App"
      description="Use a Private App access token to connect to HubSpot"
      actions={actions}
    >
      <div className="flex flex-col gap-5">
        {/* Info banner */}
        <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-3">
          <InfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">What is a Private App?</p>
            <p className="mt-1 text-blue-700">
              Private Apps allow secure API access to your HubSpot account
              without requiring OAuth authentication.{' '}
              <a
                href={HUBSPOT_PRIVATE_APPS_DOCS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-600 underline hover:text-blue-800"
              >
                Learn how to create one
                <OpenNewWindow className="h-3.5 w-3.5" />
              </a>
            </p>
          </div>
        </div>

        {/* Name field */}
        <div>
          <FieldTitle title="Connection Name *" />
          <Input
            placeholder="e.g., My HubSpot Account"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={connecting}
            maxLength={100}
          />
          <p className="mt-1 text-xs text-gray-500">
            A friendly name to identify this connection
          </p>
        </div>

        {/* Access Token field */}
        <div>
          <FieldTitle title="Private App Access Token *" />
          <Input
            type="password"
            placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            disabled={connecting}
          />
          <p className="mt-1 text-xs text-gray-500">
            Your Private App access token starting with &quot;pat-&quot;
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
